import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ArrowRight01Icon,
  Configuration01Icon,
  InformationCircleIcon,
  Loading03Icon,
  Mail01Icon,
  MailBlock01Icon,
  MailEdit01Icon,
  MailOpenLoveIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { EMAIL_TEMPLATE_TYPE_OPTIONS } from 'constants/email-config';
import patternValidators from 'helpers/validators/pattern-validators';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import BrandingTabSplitLayout from 'components/BrandingTabSplitLayout/BrandingTabSplitLayout';
import { EmailTemplatePreview } from 'components/EmailTemplatePreview/EmailTemplatePreview';
import { ImagePicker, Input, InputGroup, Select, Toggle } from 'components/FormComponents';

import {
  useGetEmailTemplateDefaultPreviewQuery,
  useGetEmailTemplatesQuery,
  useLazyGetEmailTemplateDefaultPreviewQuery,
} from 'api/email-templates-api';
import { useGetUploadQuery } from 'api/upload-api';

const DEFAULT_PREVIEW_TYPE = 'candidate_request';

const CANDIDATE_TYPES = [
  'candidate_request',
  'new_checks_added',
  'new_referee_nomination',
  'replace_referee',
  'candidate_portal_invitation',
];

const REFEREE_TYPES = ['new_request_to_referee'];

const DOTS_INTERVAL_MS = 200;

const TemplateTypeSelector = ({
  typeValue,
  currentTemplateId,
  onLinkChange,
  onPreview,
  readOnly,
  isActive = false,
}) => {
  const { t } = useTranslation();
  const typeLabel = useMemo(() => {
    const opt = EMAIL_TEMPLATE_TYPE_OPTIONS.find(o => o.value === typeValue);
    return t(`emailTemplates.type.${typeValue}`, { defaultValue: opt?.label ?? typeValue });
  }, [typeValue, t]);
  const { data: templates = [] } = useGetEmailTemplatesQuery({ type: typeValue });
  const [fetchDefaultPreview, { isFetching }] = useLazyGetEmailTemplateDefaultPreviewQuery();
  const [dots, setDots] = useState('');
  const dotsRef = useRef(null);

  useEffect(() => {
    if (isFetching) {
      dotsRef.current = setInterval(
        () => setDots(d => (d.length >= 3 ? '' : d + '.')),
        DOTS_INTERVAL_MS,
      );
    } else {
      clearInterval(dotsRef.current);
      setDots('');
    }
    return () => clearInterval(dotsRef.current);
  }, [isFetching]);

  const updatePreview = async templateId => {
    if (templateId) {
      const template = templates.find(tmpl => tmpl.id === templateId);
      if (template) onPreview(typeValue, template);
      return;
    }
    const result = await fetchDefaultPreview({ type: typeValue });
    if (result?.data) onPreview(typeValue, result.data);
  };

  const handlePreview = () => updatePreview(currentTemplateId);

  const handleSelectChange = async e => {
    const templateId = e.target.value ? Number(e.target.value) : null;
    onLinkChange(typeValue, templateId);
    await updatePreview(templateId);
  };

  return (
    <div
      className={cn(
        'flex gap-8 justify-between flex-wrap items-center p-4 border rounded-2xl',
        isActive ? 'border-primary-4 shadow-sm' : 'border-neutral-4',
      )}>
      <span
        className={cn(
          'text-2xl font-medium w-1/3',
          isFetching ? 'text-primary-4' : 'text-neutral-6',
        )}>
        {isFetching ? `Loading${dots}` : typeLabel}
      </span>
      <div className="flex gap-4 items-center flex-grow-1 justify-end">
        <InputGroup noMargin className="w-full">
          <Select
            value={currentTemplateId ?? ''}
            onChange={handleSelectChange}
            disabled={readOnly}
            className="w-full">
            <Select.Item value="">{t('company.tabs.branding.emails.noTemplate')}</Select.Item>
            {templates.map(tmpl => (
              <Select.Item key={tmpl.id} value={tmpl.id}>
                {tmpl.name}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
        <HugeiconsIcon
          icon={isFetching ? Loading03Icon : ArrowRight01Icon}
          className={cn(
            'cursor-pointer hover:text-primary-4 transition-colors duration-200',
            isFetching && 'animate-spin text-primary-4',
            !isFetching && isActive && 'text-primary-4',
            !isFetching && !isActive && 'text-neutral-5',
          )}
          aria-hidden="true"
          onClick={handlePreview}
          strokeWidth={1.5}
          size={32}
        />
      </div>
    </div>
  );
};

const TemplateGroup = ({
  icon,
  title,
  description,
  types,
  getTemplateId,
  handleLinkChange,
  onPreviewChange,
  activePreviewType,
  readOnly,
}) => (
  <div className="flex flex-col gap-8">
    <div className="flex gap-2">
      <HugeiconsIcon icon={icon} size="24" className="shrink-0 text-neutral-6" />
      <div className="flex flex-col gap-2">
        <span className="text-2xl font-medium text-neutral-7">{title}</span>
        <p className="text-xl text-text-tertiary">{description}</p>
      </div>
    </div>
    <div className="grid grid-cols-1 gap-6 md:grid-cols-1">
      {types.map(typeValue => (
        <TemplateTypeSelector
          key={typeValue}
          typeValue={typeValue}
          currentTemplateId={getTemplateId(typeValue)}
          onLinkChange={handleLinkChange}
          onPreview={onPreviewChange}
          isActive={activePreviewType === typeValue}
          readOnly={readOnly}
        />
      ))}
    </div>
  </div>
);

export const Emails = ({
  brand,
  register,
  control,
  errors,
  readOnly = false,
  backgroundColor = 'background-secondary',
  onEmailTemplateLinksChange = () => {},
}) => {
  const { t } = useTranslation();

  const [linkChanges, setLinkChanges] = useState({});
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [activePreviewType, setActivePreviewType] = useState(DEFAULT_PREVIEW_TYPE);

  const emailTemplateLinksKey = JSON.stringify(brand?.email_template_links ?? null);

  useEffect(() => {
    setLinkChanges({});
    setPreviewTemplate(null);
    setActivePreviewType(DEFAULT_PREVIEW_TYPE);
  }, [brand?.id, emailTemplateLinksKey]);

  const handlePreviewChange = (typeValue, template) => {
    setActivePreviewType(typeValue);
    setPreviewTemplate(template);
  };

  const getTemplateId = typeValue => {
    if (typeValue in linkChanges) return linkChanges[typeValue];
    return brand?.email_template_links?.[typeValue] ?? null;
  };

  const handleLinkChange = (typeValue, templateId) => {
    const next = { ...linkChanges, [typeValue]: templateId };
    setLinkChanges(next);
    onEmailTemplateLinksChange(next);
  };

  const emailCommunicationValue = useWatch({ control, name: 'use_email_communication' });
  const emailActiveValue = useWatch({ control, name: 'email_active' });
  const includeSupportFooterValue = useWatch({ control, name: 'include_support_footer' });
  const emailLogoAlignmentValue = useWatch({ control, name: 'email_logo_alignment' });
  const bannerLogoId = useWatch({ control, name: 'banner_logo_id' });
  const { data: bannerLogoUpload } = useGetUploadQuery(bannerLogoId, { skip: !bannerLogoId });

  const getEventTypeLabel = typeValue => {
    const opt = EMAIL_TEMPLATE_TYPE_OPTIONS.find(o => o.value === typeValue);
    return t(`emailTemplates.type.${typeValue}`, { defaultValue: opt?.label ?? typeValue });
  };

  const previewBrand = useMemo(() => {
    if (!brand) return null;
    const hasBannerId = bannerLogoId != null && bannerLogoId !== '';
    const resolvedBannerLogo = !hasBannerId
      ? (brand.banner_logo ?? null)
      : (bannerLogoUpload?.url ??
        (String(bannerLogoId) === String(brand.banner_logo_id) ? brand.banner_logo : null));
    return {
      ...brand,
      banner_logo_id: bannerLogoId,
      banner_logo: resolvedBannerLogo,
      email_logo_alignment: emailLogoAlignmentValue ?? brand.email_logo_alignment ?? 'left',
    };
  }, [brand, bannerLogoId, bannerLogoUpload, emailLogoAlignmentValue]);

  const candidateRequestTemplateId = getTemplateId(DEFAULT_PREVIEW_TYPE);

  const { data: candidateRequestTemplates = [] } = useGetEmailTemplatesQuery(
    { type: DEFAULT_PREVIEW_TYPE },
    { skip: !emailCommunicationValue },
  );

  const brandCandidateRequestTemplate = useMemo(() => {
    if (!candidateRequestTemplateId) return null;
    return candidateRequestTemplates.find(t => t.id === candidateRequestTemplateId) ?? null;
  }, [candidateRequestTemplates, candidateRequestTemplateId]);

  const { data: systemDefaultPreview } = useGetEmailTemplateDefaultPreviewQuery(
    { type: DEFAULT_PREVIEW_TYPE },
    { skip: !emailCommunicationValue || !!brandCandidateRequestTemplate },
  );

  const defaultPreviewTemplate = useMemo(() => {
    if (brandCandidateRequestTemplate) return brandCandidateRequestTemplate;
    if (systemDefaultPreview) {
      return {
        name: getEventTypeLabel(DEFAULT_PREVIEW_TYPE),
        subject: systemDefaultPreview.subject,
        body: systemDefaultPreview.body,
      };
    }
    return null;
  }, [brandCandidateRequestTemplate, systemDefaultPreview, t]);

  const activePreviewTemplate = previewTemplate ?? defaultPreviewTemplate;

  const showSupportFooter = includeSupportFooterValue ?? brand?.include_support_footer ?? true;

  const emailPreview = useMemo(() => {
    if (!emailCommunicationValue || !previewBrand || !activePreviewTemplate) return null;
    return (
      <EmailTemplatePreview
        brand={previewBrand}
        className="min-h-[18rem] w-full flex-1 lg:min-h-0"
        subject={activePreviewTemplate.subject}
        body={activePreviewTemplate.body}
        showSupportFooter={showSupportFooter}
        showActions
        showPreviewWarning={false}
      />
    );
  }, [emailCommunicationValue, previewBrand, activePreviewTemplate, showSupportFooter]);

  const emailPreviewTitle = useMemo(() => {
    if (!emailCommunicationValue) return 'Email Preview';
    return (
      previewTemplate?.name ??
      activePreviewTemplate?.name ??
      getEventTypeLabel(DEFAULT_PREVIEW_TYPE)
    );
  }, [emailCommunicationValue, previewTemplate?.name, activePreviewTemplate?.name, t]);

  return (
    <BrandingTabSplitLayout
      key={brand?.id}
      shellClassName={backgroundColor}
      preview={emailPreview}
      previewTitle={emailPreviewTitle}>
      <div className="flex flex-col gap-8">
        <section className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm flex justify-between items-start">
          <div className="flex gap-2 flex-grow-1 w-3/4">
            <HugeiconsIcon icon={Mail01Icon} size="24" className="shrink-0 text-neutral-6" />
            <div className="flex flex-col gap-2">
              <span className="text-2xl font-medium text-neutral-7">Email Communication</span>
              <p className="text-xl text-text-tertiary">
                {t('company.tabs.branding.emails.useEmailCommunicationDescription')}
              </p>
            </div>
          </div>
          <div className="w-auto flex-shrink-0">
            <InputGroup noMargin>
              <Toggle register={register} name="use_email_communication" small />
            </InputGroup>
          </div>
        </section>

        <section className="flex flex-col gap-8">
          {emailCommunicationValue ? (
            <>
              <article className="flex flex-col gap-8 rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm">
                <div className="flex gap-2">
                  <HugeiconsIcon
                    icon={MailEdit01Icon}
                    size="24"
                    className="shrink-0 text-neutral-6"
                  />
                  <div className="flex flex-col gap-2 mb-4">
                    <span className="text-2xl font-medium text-neutral-7">Email settings</span>
                    <p className="text-xl text-text-tertiary">
                      Configure your email settings for your brand.
                    </p>
                  </div>
                </div>

                <InputGroup
                  title="Brand Email"
                  description="This email address is used as the sender of the email."
                  noMargin>
                  <Input
                    register={register}
                    name="brand_email"
                    placeholder="no-reply@checkmate.tech"
                    validators={patternValidators.email}
                    error={
                      errors?.brand_email?.message ||
                      (brand?.brand_email && !brand?.signature_id
                        ? 'Connection problem'
                        : undefined)
                    }
                  />
                </InputGroup>

                <InputGroup
                  title="Email banner"
                  description="Ideal size is 560×190 pixels. If not provided, the brand logo will be used."
                  noMargin>
                  <Controller
                    control={control}
                    name="banner_logo_id"
                    render={({ field: { onChange, value } }) => (
                      <ImagePicker
                        onChange={onChange}
                        value={value}
                        cropRecommendedSize="560×190px"
                      />
                    )}
                  />
                </InputGroup>

                {!bannerLogoId && (
                  <InputGroup title="Email logo alignment" noMargin>
                    <Select
                      name="email_logo_alignment"
                      register={register}
                      value={emailLogoAlignmentValue ?? 'left'}
                      useDefault>
                      <Select.Item value="left">Left</Select.Item>
                      <Select.Item value="center">Center</Select.Item>
                      <Select.Item value="right">Right</Select.Item>
                    </Select>
                  </InputGroup>
                )}

                <div className="flex justify-between items-start">
                  <div className="flex gap-2 flex-grow-1 w-3/4">
                    <div className="flex flex-col gap-2">
                      <span className="text-xl font-medium text-[#514779]">Support footer</span>
                      <p className="text-lg text-secondary">
                        {t('company.tabs.branding.emails.includeSupportFooterDescription')}
                      </p>
                    </div>
                  </div>
                  <div className="w-auto flex-shrink-0">
                    <Toggle register={register} name="include_support_footer" small />
                  </div>
                </div>
              </article>

              <article className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm flex flex-col gap-8">
                <div className="flex justify-between items-start">
                  <div className="flex gap-2 flex-grow-1 w-3/4">
                    <HugeiconsIcon
                      icon={Configuration01Icon}
                      size="24"
                      className="shrink-0 text-neutral-6"
                    />
                    <div className="flex flex-col gap-2">
                      <span className="text-2xl font-medium text-neutral-7">Customisation</span>
                      <p className="text-xl text-text-tertiary">
                        Customise email templates for candidates and referees.
                      </p>
                    </div>
                  </div>
                  <div className="w-auto flex-shrink-0">
                    <InputGroup noMargin>
                      <Toggle register={register} name="email_active" small />
                    </InputGroup>
                  </div>
                </div>

                {emailActiveValue ? (
                  <div className="flex flex-col gap-8 w-full border-t border-neutral-4 pt-8">
                    <TemplateGroup
                      icon={MailOpenLoveIcon}
                      title="Candidate email templates"
                      description="Email templates for candidates for events such as candidate request, new checks added, new referee nominations, replace referee, and candidate portal invitation."
                      types={CANDIDATE_TYPES}
                      getTemplateId={getTemplateId}
                      handleLinkChange={handleLinkChange}
                      onPreviewChange={handlePreviewChange}
                      activePreviewType={activePreviewType}
                      readOnly={readOnly}
                    />
                    <div className="border-t border-neutral-4 my-4" />
                    <TemplateGroup
                      icon={MailOpenLoveIcon}
                      title="Referee email templates"
                      description="Email templates for referees for events such as new request to referee."
                      types={REFEREE_TYPES}
                      getTemplateId={getTemplateId}
                      handleLinkChange={handleLinkChange}
                      onPreviewChange={handlePreviewChange}
                      activePreviewType={activePreviewType}
                      readOnly={readOnly}
                    />
                  </div>
                ) : (
                  <div className="flex gap-2 items-center justify-center w-full border-t border-neutral-4 pt-8">
                    <HugeiconsIcon
                      icon={InformationCircleIcon}
                      size="20"
                      strokeWidth="1.5"
                      className="text-neutral-6"
                    />
                    <p className="text-xl text-text-tertiary">
                      Using standard Checkmate templates. Toggle on to customise.
                    </p>
                  </div>
                )}
              </article>
            </>
          ) : (
            <article className="rounded-3xl border border-neutral-4 bg-white p-6 shadow-sm flex flex-col gap-8">
              <div className="flex gap-2 w-full">
                <HugeiconsIcon
                  icon={Configuration01Icon}
                  size="24"
                  className="shrink-0 text-neutral-6"
                />
                <div className="flex flex-col gap-2">
                  <span className="text-2xl font-medium text-neutral-7">
                    Configuration & Customisation
                  </span>
                  <p className="text-xl text-text-tertiary">
                    Configure your email settings and customise email templates for candidates and
                    referees.
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-4 w-full border-t border-neutral-4 pt-8 items-center justify-center">
                <HugeiconsIcon
                  icon={MailBlock01Icon}
                  size="80"
                  strokeWidth="1.5"
                  className="text-neutral-6"
                />
                <div className="flex gap-2 items-center justify-center w-full">
                  <HugeiconsIcon
                    icon={InformationCircleIcon}
                    size="20"
                    strokeWidth="1.5"
                    className="text-neutral-6"
                  />
                  <p className="text-xl text-text-tertiary">
                    Email communication is disabled. Please enable it to configure your email
                    settings and customise email templates.
                  </p>
                </div>
              </div>
            </article>
          )}
        </section>
      </div>
    </BrandingTabSplitLayout>
  );
};
